import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import {
  AdminBillingPlanItem,
  AdminPlayerBehavior,
  AdminPlayerDetail,
  AdminPlayerFinancialTrail,
  AdminPlayerListItem,
  AdminPlayerOwnerAccess,
  AdminPlayerOwnerAccessInput,
} from '../types';

const formatAmount = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatNumber = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(value || 0);

const formatPercent = (value: number): string => `${formatNumber(value)}%`;

const formatDuration = (value: number): string => {
  const totalSeconds = Math.max(0, Math.trunc(value || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }

  if (minutes > 0) {
    return `${minutes}min`;
  }

  return `${totalSeconds}s`;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatDateInput = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const OWNER_ACCESS_STATUS_OPTIONS: AdminPlayerOwnerAccessInput['status'][] = [
  'TRIAL',
  'ACTIVE',
  'SUSPENDED',
  'CANCELLED',
  'EXPIRED',
];

const PlayersPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState<AdminPlayerListItem[]>([]);
  const [detail, setDetail] = useState<AdminPlayerDetail | null>(null);
  const [financialTrail, setFinancialTrail] = useState<AdminPlayerFinancialTrail | null>(null);
  const [behavior, setBehavior] = useState<AdminPlayerBehavior | null>(null);
  const [ownerAccess, setOwnerAccess] = useState<AdminPlayerOwnerAccess | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [financialTrailError, setFinancialTrailError] = useState('');
  const [behaviorError, setBehaviorError] = useState('');
  const [ownerAccessError, setOwnerAccessError] = useState('');
  const [ownerSaveError, setOwnerSaveError] = useState('');
  const [ownerSaveLoading, setOwnerSaveLoading] = useState(false);
  const [planCatalog, setPlanCatalog] = useState<AdminBillingPlanItem[]>([]);
  const [planCatalogError, setPlanCatalogError] = useState('');
  const [ownerStatus, setOwnerStatus] = useState<AdminPlayerOwnerAccessInput['status']>('TRIAL');
  const [ownerPlanCode, setOwnerPlanCode] = useState('CLUB_OWNER_MONTHLY');
  const [ownerExpiresAt, setOwnerExpiresAt] = useState('');
  const [ownerNotes, setOwnerNotes] = useState('');
  const [ownerReason, setOwnerReason] = useState('');

  const loadData = useCallback(async (nextSearch = '') => {
    setLoading(true);
    setError('');

    try {
      const payload = await adminService.listPlayers(nextSearch);
      setPlayers(payload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar jogadores');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlanCatalog = useCallback(async () => {
    setPlanCatalogError('');

    try {
      const payload = await adminService.getBillingPlans();
      setPlanCatalog(payload.items);
    } catch (nextError: any) {
      setPlanCatalog([]);
      setPlanCatalogError(nextError.message || 'Erro ao carregar o catalogo oficial de planos');
    }
  }, []);

  const loadDetail = useCallback(async (userId: string) => {
    setSelectedUserId(userId);
    setDetailLoading(true);
    setDetail(null);
    setFinancialTrail(null);
    setBehavior(null);
    setOwnerAccess(null);
    setDetailError('');
    setFinancialTrailError('');
    setBehaviorError('');
    setOwnerAccessError('');
    setOwnerSaveError('');

    const [detailResult, trailResult, behaviorResult, ownerAccessResult] = await Promise.allSettled([
      adminService.getPlayerDetail(userId),
      adminService.getPlayerFinancialTrail(userId),
      adminService.getPlayerBehavior(userId),
      adminService.getPlayerOwnerAccess(userId),
    ]);

    if (detailResult.status === 'fulfilled') {
      setDetail(detailResult.value);
    } else {
      setDetailError(detailResult.reason?.message || 'Erro ao carregar detalhe do jogador');
    }

    if (trailResult.status === 'fulfilled') {
      setFinancialTrail(trailResult.value);
    } else {
      setFinancialTrailError(
        trailResult.reason?.message || 'Erro ao carregar trilha financeira do jogador'
      );
    }

    if (behaviorResult.status === 'fulfilled') {
      setBehavior(behaviorResult.value);
    } else {
      setBehaviorError(behaviorResult.reason?.message || 'Erro ao carregar comportamento do jogador');
    }

    if (ownerAccessResult.status === 'fulfilled') {
      const nextOwnerAccess = ownerAccessResult.value;
      const nextStatus =
        nextOwnerAccess.subscription.subscription_status ||
        (nextOwnerAccess.summary.owned_clubs_count > 0 ? 'ACTIVE' : 'TRIAL');

      setOwnerAccess(nextOwnerAccess);
      setOwnerStatus(nextStatus);
      setOwnerPlanCode(nextOwnerAccess.subscription.plan_code || 'CLUB_OWNER_MONTHLY');
      setOwnerExpiresAt(formatDateInput(nextOwnerAccess.subscription.expires_at));
      setOwnerNotes(nextOwnerAccess.subscription.notes || '');
      setOwnerReason('');
    } else {
      setOwnerAccessError(
        ownerAccessResult.reason?.message || 'Erro ao carregar a governanca de operador do jogador'
      );
      setOwnerStatus('TRIAL');
      setOwnerPlanCode('CLUB_OWNER_MONTHLY');
      setOwnerExpiresAt('');
      setOwnerNotes('');
      setOwnerReason('');
    }

    setDetailLoading(false);
  }, []);

  const handleSaveOwnerAccess = useCallback(async () => {
    if (!selectedUserId) return;

    const reason = ownerReason.trim();
    if (!reason) {
      setOwnerSaveError('Informe o motivo obrigatorio para governar o operador.');
      return;
    }

    setOwnerSaveLoading(true);
    setOwnerSaveError('');

    try {
      const nextOwnerAccess = await adminService.upsertPlayerOwnerAccess(selectedUserId, {
        status: ownerStatus,
        planCode: ownerPlanCode.trim() || 'CLUB_OWNER_MONTHLY',
        expiresAt: ownerExpiresAt || null,
        notes: ownerNotes.trim() || null,
        reason,
      });

      setOwnerAccess(nextOwnerAccess);
      setOwnerStatus(nextOwnerAccess.subscription.subscription_status || ownerStatus);
      setOwnerPlanCode(nextOwnerAccess.subscription.plan_code || 'CLUB_OWNER_MONTHLY');
      setOwnerExpiresAt(formatDateInput(nextOwnerAccess.subscription.expires_at));
      setOwnerNotes(nextOwnerAccess.subscription.notes || '');
      setOwnerReason('');
      setDetail((current) =>
        current && current.profile.user_id === nextOwnerAccess.profile.user_id
          ? {
              ...current,
              profile: {
                ...current.profile,
                role: nextOwnerAccess.profile.role,
              },
            }
          : current
      );
      await loadData(search);
    } catch (nextError: any) {
      setOwnerSaveError(nextError.message || 'Erro ao governar o acesso de operador');
    } finally {
      setOwnerSaveLoading(false);
    }
  }, [
    loadData,
    ownerExpiresAt,
    ownerNotes,
    ownerPlanCode,
    ownerReason,
    ownerStatus,
    search,
    selectedUserId,
  ]);

  useEffect(() => {
    void loadData('');
    void loadPlanCatalog();
  }, [loadData, loadPlanCatalog]);

  const ownerPlanOptions = useMemo(() => {
    const officialPlans = planCatalog.filter((plan) => plan.source_type === 'PERSISTED' && plan.is_active);
    const currentPlan = planCatalog.find((plan) => plan.plan_code === ownerPlanCode) || null;

    if (currentPlan && !officialPlans.some((plan) => plan.plan_code === currentPlan.plan_code)) {
      return [currentPlan, ...officialPlans];
    }

    return officialPlans;
  }, [ownerPlanCode, planCatalog]);

  const metricCards = useMemo(
    () =>
      detail
        ? [
            {
              eyebrow: 'Clubs',
              title: 'Vinculos ativos',
              value: String(detail.summary.active_clubs),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Carteira',
              title: 'Saldo global',
              value: formatAmount(detail.summary.global_balance),
              tone: 'emerald' as const,
            },
            {
              eyebrow: 'Clubs',
              title: 'Saldo distribuido',
              value: formatAmount(detail.summary.club_balance_total),
              tone: 'slate' as const,
            },
            {
              eyebrow: 'Financeiro',
              title: 'Recebiveis abertos',
              value: formatAmount(detail.summary.open_receivables),
              tone: 'amber' as const,
            },
            {
              eyebrow: 'Jogo',
              title: 'Maos 30d',
              value: String(detail.summary.hands_played_30d),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Resultado',
              title: 'Lucro 30d',
              value: formatAmount(detail.summary.profit_30d),
              tone: 'rose' as const,
            },
          ]
        : [],
    [detail]
  );

  const profitSeriesPreview = useMemo(
    () => (behavior ? behavior.charts.profit_series.slice(-8) : []),
    [behavior]
  );

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Players</p>
          <h1>Jogadores da plataforma</h1>
          <p>Busca global com visao 360 de carteira, clubes, atividade, trilha financeira e comportamento.</p>
        </div>
        <div className="admin-topbar__actions">
          {detail ? <span className="admin-chip">Selecionado: {detail.profile.user_name}</span> : null}
          <button type="button" className="admin-secondary-button" onClick={() => void loadData(search)} disabled={loading}>
            {loading ? 'Buscando...' : 'Atualizar lista'}
          </button>
        </div>
      </section>

      <section className="admin-section-card">
        <div className="admin-toolbar">
          <input
            className="admin-search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nickname, email ou CPF"
          />
          <button type="button" className="admin-secondary-button" onClick={() => void loadData(search)} disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Papel</th>
                <th>Clubs</th>
                <th>Depositos</th>
                <th>Saques</th>
                <th>Sessao ativa</th>
                <th>Saque aberto</th>
                <th>Criado</th>
                <th>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {players.length ? (
                players.map((player) => (
                  <tr key={player.user_id} className={selectedUserId === player.user_id ? 'admin-table__row--selected' : ''}>
                    <td>
                      <strong>{player.user_name}</strong>
                      <span>{player.email}</span>
                    </td>
                    <td>{player.role}</td>
                    <td>{player.total_clubs}</td>
                    <td>{formatAmount(player.total_confirmed_deposits)}</td>
                    <td>{formatAmount(player.total_paid_withdrawals)}</td>
                    <td>{player.open_table_sessions}</td>
                    <td>{player.open_withdrawal_requests}</td>
                    <td>{formatDateTime(player.created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-link-chip admin-link-chip--button"
                        onClick={() => void loadDetail(player.user_id)}
                        disabled={detailLoading && selectedUserId === player.user_id}
                      >
                        {selectedUserId === player.user_id ? (detailLoading ? 'Carregando...' : 'Recarregar') : 'Detalhar'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>Nenhum jogador encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {detailError ? <div className="admin-banner admin-banner--error">{detailError}</div> : null}
      {ownerAccessError ? <div className="admin-banner admin-banner--error">{ownerAccessError}</div> : null}
      {planCatalogError ? <div className="admin-banner admin-banner--error">{planCatalogError}</div> : null}

      {detail ? (
        <>
          <section className="admin-metrics-grid">
            {metricCards.map((card) => (
              <MetricCard key={card.title} {...card} />
            ))}
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Perfil</p>
                  <h2>Identidade e suporte</h2>
                </div>
                <span className="admin-chip">Atualizado em {formatDateTime(detail.generated_at)}</span>
              </div>

              <div className="admin-detail-grid">
                <div className="admin-detail-pair">
                  <span>Nome</span>
                  <strong>{detail.profile.user_name}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Email</span>
                  <strong>{detail.profile.email || '-'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>CPF</span>
                  <strong>{detail.profile.cpf || '-'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Telefone</span>
                  <strong>{detail.profile.phone || '-'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Papel</span>
                  <strong>{detail.profile.role}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Conta</span>
                  <strong>{detail.profile.is_banned ? 'BLOQUEADA' : 'ATIVA'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Ultimo login</span>
                  <strong>{formatDateTime(detail.profile.last_login)}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Criado em</span>
                  <strong>{formatDateTime(detail.profile.created_at)}</strong>
                </div>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Resumo</p>
                  <h2>Carteira, exposicao e atividade</h2>
                </div>
              </div>

              <div className="admin-kpi-grid">
                <article className="admin-kpi-item">
                  <span>Clubs totais</span>
                  <strong>{detail.summary.total_clubs}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Depositos totais</span>
                  <strong>{formatAmount(detail.summary.total_confirmed_deposits)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Saques totais</span>
                  <strong>{formatAmount(detail.summary.total_paid_withdrawals)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Sessoes abertas</span>
                  <strong>{detail.summary.open_table_sessions}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Saques abertos</span>
                  <strong>{detail.summary.open_withdrawal_requests}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Payables abertas</span>
                  <strong>{formatAmount(detail.summary.open_payables)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Clubs jogados 30d</span>
                  <strong>{detail.summary.clubs_played_30d}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Ultima mao</span>
                  <strong>{formatDateTime(detail.summary.last_hand_played_at)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Ultima atividade</span>
                  <strong>{formatDateTime(detail.summary.last_activity_at)}</strong>
                </article>
              </div>
            </article>
          </section>

          {ownerAccess ? (
            <section className="admin-grid admin-grid--two">
              <article className="admin-section-card">
                <div className="admin-section-card__header">
                  <div>
                    <p className="admin-section-eyebrow">Operador</p>
                    <h2>Estado atual de ownership</h2>
                  </div>
                  <span className="admin-chip">
                    {ownerAccess.subscription.effective_subscription_status || 'SEM ASSINATURA'}
                  </span>
                </div>

                <div className="admin-detail-grid">
                  <div className="admin-detail-pair">
                    <span>Role atual</span>
                    <strong>{ownerAccess.profile.role}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Plano</span>
                    <strong>{ownerAccess.subscription.plan_code || '-'}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Clubs como owner</span>
                    <strong>{ownerAccess.summary.owned_clubs_count}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Clubs ativos como owner</span>
                    <strong>{ownerAccess.summary.active_owned_clubs_count}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Pode criar club</span>
                    <strong>{ownerAccess.summary.can_create_club ? 'SIM' : 'NAO'}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Pode operar club</span>
                    <strong>{ownerAccess.summary.can_operate_club ? 'SIM' : 'NAO'}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Expiracao</span>
                    <strong>{formatDateTime(ownerAccess.subscription.expires_at)}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Ultimo pagamento</span>
                    <strong>{formatDateTime(ownerAccess.subscription.last_payment_at)}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Atualizado por</span>
                    <strong>{ownerAccess.subscription.created_by_name || '-'}</strong>
                  </div>
                  <div className="admin-detail-pair">
                    <span>Atualizado em</span>
                    <strong>{formatDateTime(ownerAccess.generated_at)}</strong>
                  </div>
                </div>

                {ownerAccess.notes.length ? (
                  <div className="admin-note-stack">
                    {ownerAccess.notes.map((note) => (
                      <div key={note} className="admin-note-chip">
                        {note}
                      </div>
                    ))}
                  </div>
                ) : null}
              </article>

              <article className="admin-section-card">
                <div className="admin-section-card__header">
                  <div>
                    <p className="admin-section-eyebrow">Governanca</p>
                    <h2>Ativar operador do club</h2>
                  </div>
                  <span className="admin-chip">{selectedUserId || '-'}</span>
                </div>

                <div className="admin-form-grid">
                  <label className="admin-form-field">
                    <span>Status comercial</span>
                    <select
                      className="admin-form-control"
                      value={ownerStatus}
                      onChange={(event) =>
                        setOwnerStatus(event.target.value as AdminPlayerOwnerAccessInput['status'])
                      }
                      disabled={ownerSaveLoading}
                    >
                      {OWNER_ACCESS_STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="admin-form-field">
                    <span>Plano</span>
                    <select
                      className="admin-form-control"
                      value={ownerPlanCode}
                      onChange={(event) => setOwnerPlanCode(event.target.value)}
                      disabled={ownerSaveLoading}
                    >
                      {ownerPlanOptions.length ? (
                        ownerPlanOptions.map((plan) => (
                          <option key={plan.plan_code} value={plan.plan_code}>
                            {plan.display_name} ({plan.plan_code})
                          </option>
                        ))
                      ) : (
                        <option value={ownerPlanCode || 'CLUB_OWNER_MONTHLY'}>
                          {ownerPlanCode || 'CLUB_OWNER_MONTHLY'}
                        </option>
                      )}
                    </select>
                  </label>

                  <label className="admin-form-field">
                    <span>Expira em</span>
                    <input
                      type="date"
                      className="admin-form-control"
                      value={ownerExpiresAt}
                      onChange={(event) => setOwnerExpiresAt(event.target.value)}
                      disabled={ownerSaveLoading}
                    />
                  </label>

                  <label className="admin-form-field">
                    <span>Notas operacionais</span>
                    <input
                      className="admin-form-control"
                      value={ownerNotes}
                      onChange={(event) => setOwnerNotes(event.target.value)}
                      placeholder="Ex.: trial inicial liberado pelo SaaS"
                      disabled={ownerSaveLoading}
                    />
                  </label>

                  <label className="admin-form-field admin-form-field--full">
                    <span>Motivo da mudanca</span>
                    <textarea
                      className="admin-form-control admin-form-control--textarea"
                      value={ownerReason}
                      onChange={(event) => setOwnerReason(event.target.value)}
                      placeholder="Explique por que este jogador esta sendo habilitado ou atualizado como operador."
                      disabled={ownerSaveLoading}
                    />
                  </label>
                </div>

                {ownerSaveError ? (
                  <div className="admin-banner admin-banner--error">{ownerSaveError}</div>
                ) : null}

                <div className="admin-filter-actions">
                  <button
                    type="button"
                    className="admin-primary-button admin-primary-button--inline"
                    onClick={() => void handleSaveOwnerAccess()}
                    disabled={ownerSaveLoading}
                  >
                    {ownerSaveLoading ? 'Salvando...' : 'Salvar governanca'}
                  </button>
                </div>

                <div className="admin-note-stack">
                  <div className="admin-note-chip">
                    Esta governanca libera a capacidade comercial de criar e operar clubs.
                  </div>
                  <div className="admin-note-chip">
                    O plano agora vem do catalogo oficial governado no billing do portal admin.
                  </div>
                  <div className="admin-note-chip">
                    Ela nao transfere ownership de um club existente. A posse real continua em
                    clubs.owner_id.
                  </div>
                </div>
              </article>
            </section>
          ) : null}

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Clubs</p>
                  <h2>Vinculos e carteira por club</h2>
                </div>
                <span className="admin-chip">{detail.clubs.length} club(s)</span>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Club</th>
                      <th>Membro</th>
                      <th>Carteira</th>
                      <th>Depositos 30d</th>
                      <th>Saques 30d</th>
                      <th>Exposicao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.clubs.length ? (
                      detail.clubs.map((club) => (
                        <tr key={club.club_id}>
                          <td>
                            <strong>{club.club_name}</strong>
                            <span>{club.club_code}</span>
                          </td>
                          <td>
                            <strong>{club.membership_role}</strong>
                            <span>{club.membership_status}</span>
                          </td>
                          <td>
                            <strong>{formatAmount(club.wallet_balance)}</strong>
                            <span>{club.club_is_active ? 'Club ativo' : 'Club inativo'}</span>
                          </td>
                          <td>{formatAmount(club.confirmed_deposits_30d)}</td>
                          <td>
                            <strong>{formatAmount(club.paid_withdrawals_30d)}</strong>
                            <span>{club.open_withdrawal_requests} em aberto</span>
                          </td>
                          <td>
                            <strong>Rec: {formatAmount(club.open_receivable)}</strong>
                            <span>Pay: {formatAmount(club.open_payable)}</span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>Nenhum club vinculado a este jogador.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Financeiro</p>
                  <h2>Eventos recentes do jogador</h2>
                </div>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Tipo</th>
                      <th>Club</th>
                      <th>Status</th>
                      <th>Valor</th>
                      <th>Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.recent_finance.length ? (
                      detail.recent_finance.map((entry) => (
                        <tr key={entry.id}>
                          <td>{formatDateTime(entry.requested_at)}</td>
                          <td>
                            <strong>{entry.kind}</strong>
                            <span>{formatDateTime(entry.settled_at)}</span>
                          </td>
                          <td>{entry.club_name}</td>
                          <td>{entry.status}</td>
                          <td>{formatAmount(entry.amount_reais)}</td>
                          <td>{formatAmount(entry.fee_reais)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>Nenhum evento financeiro recente foi encontrado.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          </section>

          {financialTrailError ? (
            <div className="admin-banner admin-banner--error">{financialTrailError}</div>
          ) : null}

          {financialTrail ? (
            <>
              <section className="admin-section-card">
                <div className="admin-section-card__header">
                  <div>
                    <p className="admin-section-eyebrow">Finance trail</p>
                    <h2>Trilha financeira consolidada</h2>
                  </div>
                  <span className="admin-chip">
                    Atualizado em {formatDateTime(financialTrail.generated_at)}
                  </span>
                </div>

                <div className="admin-kpi-grid">
                  <article className="admin-kpi-item">
                    <span>Depositos confirmados</span>
                    <strong>{formatAmount(financialTrail.summary.total_confirmed_deposits)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Saques pagos</span>
                    <strong>{formatAmount(financialTrail.summary.total_paid_withdrawals)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Fluxo liquido</span>
                    <strong>{formatAmount(financialTrail.summary.net_cash_flow)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Fee em depositos</span>
                    <strong>{formatAmount(financialTrail.summary.deposit_fee_total)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Fee em saques</span>
                    <strong>{formatAmount(financialTrail.summary.withdrawal_fee_total)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Recebiveis abertos</span>
                    <strong>{formatAmount(financialTrail.summary.open_receivables)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Payables abertas</span>
                    <strong>{formatAmount(financialTrail.summary.open_payables)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Fila de saque</span>
                    <strong>
                      {financialTrail.summary.pending_withdrawals_count} /{' '}
                      {formatAmount(financialTrail.summary.pending_withdrawals_amount)}
                    </strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Ultima atividade financeira</span>
                    <strong>{formatDateTime(financialTrail.summary.last_financial_activity_at)}</strong>
                  </article>
                </div>
              </section>

              <section className="admin-grid admin-grid--two">
                <article className="admin-section-card">
                  <div className="admin-section-card__header">
                    <div>
                      <p className="admin-section-eyebrow">Por club</p>
                      <h2>Breakdown financeiro</h2>
                    </div>
                  </div>

                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Club</th>
                          <th>Carteira</th>
                          <th>Depositos</th>
                          <th>Saques</th>
                          <th>Exposicao</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financialTrail.club_breakdown.length ? (
                          financialTrail.club_breakdown.map((club) => (
                            <tr key={club.club_id}>
                              <td>
                                <strong>{club.club_name}</strong>
                                <span>{club.club_code}</span>
                              </td>
                              <td>
                                <strong>{formatAmount(club.wallet_balance)}</strong>
                                <span>{formatDateTime(club.last_financial_activity_at)}</span>
                              </td>
                              <td>
                                <strong>{formatAmount(club.total_confirmed_deposits)}</strong>
                                <span>Fee: {formatAmount(club.deposit_fee_total)}</span>
                              </td>
                              <td>
                                <strong>{formatAmount(club.total_paid_withdrawals)}</strong>
                                <span>
                                  Fee: {formatAmount(club.withdrawal_fee_total)} / {club.pending_withdrawals_count} abertos
                                </span>
                              </td>
                              <td>
                                <strong>Rec: {formatAmount(club.open_receivable)}</strong>
                                <span>Pay: {formatAmount(club.open_payable)}</span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5}>Nenhum club com trilha financeira encontrada.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </article>

                <article className="admin-section-card">
                  <div className="admin-section-card__header">
                    <div>
                      <p className="admin-section-eyebrow">Historico</p>
                      <h2>Entradas recentes do trail</h2>
                    </div>
                  </div>

                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Quando</th>
                          <th>Origem</th>
                          <th>Club</th>
                          <th>Status</th>
                          <th>Valor</th>
                          <th>Liquidado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financialTrail.entries.length ? (
                          financialTrail.entries.map((entry) => (
                            <tr key={entry.id}>
                              <td>{formatDateTime(entry.occurred_at)}</td>
                              <td>
                                <strong>{entry.source_type}</strong>
                                <span>{entry.reference_label || entry.details || '-'}</span>
                              </td>
                              <td>{entry.club_name}</td>
                              <td>{entry.status}</td>
                              <td>
                                <strong>{formatAmount(entry.amount_reais)}</strong>
                                <span>Fee: {formatAmount(entry.fee_reais)}</span>
                              </td>
                              <td>
                                <strong>{formatAmount(entry.settled_amount_reais)}</strong>
                                <span>Aberto: {formatAmount(entry.outstanding_amount_reais)}</span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6}>Nenhum registro financeiro consolidado foi encontrado.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </article>
              </section>

              <section className="admin-section-card">
                <div className="admin-section-card__header">
                  <div>
                    <p className="admin-section-eyebrow">Escopo financeiro</p>
                    <h2>Limites desta trilha</h2>
                  </div>
                </div>

                <div className="admin-note-stack">
                  {financialTrail.notes.map((note) => (
                    <div key={note} className="admin-note-chip">
                      {note}
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : null}

          {behaviorError ? <div className="admin-banner admin-banner--error">{behaviorError}</div> : null}

          {behavior ? (
            <>
              <section className="admin-section-card">
                <div className="admin-section-card__header">
                  <div>
                    <p className="admin-section-eyebrow">Behavior</p>
                    <h2>Comportamento de jogo em 30 dias</h2>
                  </div>
                  <span className="admin-chip">
                    Janela: {formatDateTime(behavior.period.from)} ate {formatDateTime(behavior.period.to)}
                  </span>
                </div>

                <div className="admin-kpi-grid">
                  <article className="admin-kpi-item">
                    <span>Maos jogadas</span>
                    <strong>{behavior.summary.hands_played}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Lucro total</span>
                    <strong>{formatAmount(behavior.summary.profit_total_reais)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Winrate BB/100</span>
                    <strong>{formatNumber(behavior.summary.winrate_bb_100)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Tempo jogado</span>
                    <strong>{formatDuration(behavior.summary.time_played_seconds)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Dias ativos</span>
                    <strong>{behavior.summary.active_days}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Clubs jogados</span>
                    <strong>{behavior.summary.clubs_played}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>VPIP</span>
                    <strong>{formatPercent(behavior.style.vpip_pct)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>PFR</span>
                    <strong>{formatPercent(behavior.style.pfr_pct)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Aggression factor</span>
                    <strong>{formatNumber(behavior.style.aggression_factor)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>3-bet</span>
                    <strong>{formatPercent(behavior.advanced.three_bet_pct)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>C-bet</span>
                    <strong>{formatPercent(behavior.advanced.c_bet_pct)}</strong>
                  </article>
                  <article className="admin-kpi-item">
                    <span>Fold to 3-bet</span>
                    <strong>{formatPercent(behavior.advanced.fold_to_three_bet_pct)}</strong>
                  </article>
                </div>
              </section>

              <section className="admin-grid admin-grid--two">
                <article className="admin-section-card">
                  <div className="admin-section-card__header">
                    <div>
                      <p className="admin-section-eyebrow">Performance</p>
                      <h2>Clubs e serie recente</h2>
                    </div>
                  </div>

                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Club</th>
                          <th>Maos</th>
                          <th>Lucro</th>
                          <th>Winrate BB/100</th>
                        </tr>
                      </thead>
                      <tbody>
                        {behavior.club_breakdown.length ? (
                          behavior.club_breakdown.map((club) => (
                            <tr key={club.club_id}>
                              <td>{club.club_name}</td>
                              <td>{club.hands_played}</td>
                              <td>{formatAmount(club.profit_reais)}</td>
                              <td>{formatNumber(club.winrate_bb_100)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4}>Nenhum historico de jogo consolidado no periodo.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="admin-table-wrap" style={{ marginTop: 18 }}>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Ponto</th>
                          <th>Maos</th>
                          <th>Lucro</th>
                          <th>Acumulado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profitSeriesPreview.length ? (
                          profitSeriesPreview.map((point) => (
                            <tr key={point.key}>
                              <td>{point.label}</td>
                              <td>{point.hands_played}</td>
                              <td>{formatAmount(point.profit_reais)}</td>
                              <td>{formatAmount(point.cumulative_profit_reais)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4}>Sem serie de lucro disponivel para este recorte.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </article>

                <article className="admin-section-card">
                  <div className="admin-section-card__header">
                    <div>
                      <p className="admin-section-eyebrow">Operacao</p>
                      <h2>Sessoes recentes e leitura rapida</h2>
                    </div>
                  </div>

                  <div className="admin-kpi-grid">
                    <article className="admin-kpi-item">
                      <span>Maos vencedoras</span>
                      <strong>{behavior.summary.winning_hands}</strong>
                    </article>
                    <article className="admin-kpi-item">
                      <span>Maos perdedoras</span>
                      <strong>{behavior.summary.losing_hands}</strong>
                    </article>
                    <article className="admin-kpi-item">
                      <span>Breakeven</span>
                      <strong>{behavior.summary.breakeven_hands}</strong>
                    </article>
                    <article className="admin-kpi-item">
                      <span>Fold to c-bet</span>
                      <strong>{formatPercent(behavior.advanced.fold_to_c_bet_pct)}</strong>
                    </article>
                  </div>

                  <div className="admin-table-wrap" style={{ marginTop: 18 }}>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Club</th>
                          <th>Mesa</th>
                          <th>Status</th>
                          <th>Duracao</th>
                          <th>Heartbeat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {behavior.recent_sessions.length ? (
                          behavior.recent_sessions.map((session) => (
                            <tr key={session.id}>
                              <td>
                                <strong>{session.club_name}</strong>
                                <span>{session.device_id}</span>
                              </td>
                              <td>{session.table_name}</td>
                              <td>{session.status}</td>
                              <td>{formatDuration(session.duration_seconds)}</td>
                              <td>{formatDateTime(session.last_heartbeat_at || session.closed_at || session.opened_at)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5}>Nenhuma sessao recente encontrada para o jogador.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </article>
              </section>

              <section className="admin-section-card">
                <div className="admin-section-card__header">
                  <div>
                    <p className="admin-section-eyebrow">Insights</p>
                    <h2>Sinais operacionais do recorte</h2>
                  </div>
                </div>

                <div className="admin-note-stack">
                  {behavior.insights.length ? (
                    behavior.insights.map((insight) => (
                      <div key={`${insight.tone}-${insight.title}`} className="admin-note-chip">
                        <strong>{insight.title}</strong>: {insight.description}
                      </div>
                    ))
                  ) : (
                    <div className="admin-note-chip">
                      Ainda nao ha sinal suficiente para produzir insights resumidos neste recorte.
                    </div>
                  )}
                  {behavior.notes.map((note) => (
                    <div key={note} className="admin-note-chip">
                      {note}
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : null}

          <section className="admin-section-card">
            <div className="admin-section-card__header">
              <div>
                <p className="admin-section-eyebrow">Escopo</p>
                <h2>Limites desta leitura</h2>
              </div>
            </div>

            <div className="admin-note-stack">
              {detail.notes.map((note) => (
                <div key={note} className="admin-note-chip">
                  {note}
                </div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <section className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Detalhe</p>
              <h2>Selecione um jogador para abrir a visao 360</h2>
            </div>
            <span className="admin-chip">{detailLoading ? 'Carregando detalhe' : 'Sem selecao'}</span>
          </div>

          <div className="admin-note-stack">
            <div className="admin-note-chip">A tela continua centrada na busca global e adiciona investigacao no mesmo fluxo.</div>
            <div className="admin-note-chip">O detalhe cruza saldo, clubes, exposicao, trilha financeira e comportamento sem abrir mutacoes.</div>
            <div className="admin-note-chip">Fraude, score de risco e grafo de relacoes ficam para a fase de security.</div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PlayersPage;
